import { getDelegationStore } from '../storage/stores';
import { refreshProtectionStatus } from '../protectionStatus';
import { StartDelegationError } from 'inter-extension-control/module/src/errors/StartDelegationError';
import { StopDelegationError } from 'inter-extension-control/module/src/errors/StopDelegationError';
import { getExtensionVersion } from '../chrome/api';
import { GenericError } from 'inter-extension-control/module/src/errors/GenericError';
import { captureException, Severity } from '../helper/sentry';
import { reportDelegationStatusIfChanged } from '../device/report';

export type DelegationStatus = 'START' | 'STOP';

/**
 * Returns if is logged in with delegation.
 *
 * @returns
 * TRUE if the device is logged-in with delegation.
 * FALSE otherwise.
 */
export const isLoggedInWithDelegation = () => getDelegationStore().isLoggedIn();

/**
 * Returns if delegation started.
 *
 * @returns
 * TRUE if the device is logged-in with delegation and delegation was started by Linewize.
 * FALSE otherwise.
 */
export const isDelegationStarted = () =>
  isLoggedInWithDelegation() && getDelegationStore().isDelegationStarted();

/**
 * Returns if delegation stopped.
 *
 * @returns
 * TRUE if the device is logged-in with delegation and delegation was stopped by Linewize.
 * FALSE otherwise.
 */
export const isDelegationStopped = () =>
  isLoggedInWithDelegation() && !getDelegationStore().isDelegationStarted();

/**
 * Returns if delegation started or not logged-in with delegation.
 *
 * @returns
 * TRUE if delegation started or the device is not logged-in with delegation.
 * FALSE otherwise.
 */
export const isDelegationStartedOrNotLoggedInWithDelegation = () =>
  !isLoggedInWithDelegation() || isDelegationStarted();

/**
 * Returns if the delegation is active or not.
 *
 * @throws GenericError of type 'NOT_LOGGED_IN'
 * This exception is thrown if the extension it's not logged-in with delegation.
 */
export const isDelegationActive = async () => {
  if (!isLoggedInWithDelegation()) {
    const error = new GenericError(
      'NOT_LOGGED_IN',
      getExtensionVersion(),
      'Delegation status check failed because the extension is not logged-in.'
    );
    console.error(error);
    captureException(error);
    throw error;
  }

  return getDelegationStore().isDelegationStarted();
};

/**
 * Starts or stops the delegation.
 *
 * @param status - 'START' or 'STOP'
 *
 * @throws GenericError of type 'NOT_LOGGED_IN'
 * This exception is thrown if the extension it's not logged-in with delegation.
 *
 * @throws StartDelegationError
 * This exception is thrown when starting the delegation when it's already started.
 *
 * @throws StopDelegationError
 * This exception is thrown when stopping the delegation when it's already stopped.
 */
export const updateDelegation = (status: DelegationStatus) => {
  if (!isLoggedInWithDelegation()) {
    const error = new GenericError(
      'NOT_LOGGED_IN',
      getExtensionVersion(),
      'Delegation update failed because the extension is not logged-in.'
    );
    console.warn(error);
    captureException(error, { level: Severity.Warning });
    return Promise.reject(error);
  }

  switch (status) {
    case 'START':
      return startDelegation();
    case 'STOP':
      return stopDelegation();
  }
};

const startDelegation = async () => {
  if (getDelegationStore().isDelegationStarted()) {
    const error = new StartDelegationError(
      'ALREADY_DELEGATED',
      getExtensionVersion(),
      "Delegation start failed because it's already started."
    );
    console.warn(error);
    captureException(error, { level: Severity.Warning });
    throw error;
  }
  await updateDelegationState(true);
  console.log(`Delegation was started!`);
};

const stopDelegation = async () => {
  if (!getDelegationStore().isDelegationStarted()) {
    const error = new StopDelegationError(
      'NOT_DELEGATED',
      getExtensionVersion(),
      "Delegation stop failed because it's already stopped."
    );
    console.warn(error);
    captureException(error, { level: Severity.Warning });
    throw error;
  }
  await updateDelegationState(false);
  console.log(`Delegation was stopped!`);
};

const updateDelegationState = async (isDelegationStarted: boolean) => {
  await getDelegationStore().setIsDelegationStarted(isDelegationStarted);
  // Report state to device options
  await reportDelegationStatusIfChanged(isDelegationStarted);
  // We need to refresh the protection state, including checking if the device is in a Safe Network or not,
  // because Safe Network status is never checked after stopping delegation.
  const context = isDelegationStarted ? 'delegation-start' : 'delegation-stop';
  return refreshProtectionStatus(true, context);
};
